import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { Search, Plus, X, Check, AlertCircle, Edit2, Save, ArrowLeft, Trash2 } from 'lucide-react'

const ICONS = {
  dossard: String.fromCodePoint(0x1F51F),
  repas: String.fromCodePoint(0x1F354),
  lot_coureur: String.fromCodePoint(0x1F381),
  lot_tombola: String.fromCodePoint(0x1F39F),
}

const CHAMPS_VIDES = {
  dossard: '', nom: '', prenom: '', course_id: '',
  genre: '', type_vtt: '', nb_repas: 0,
  dob: '', club: '', email: '', telephone: '',
  cp: '', paye: false, paye_par: '', prix_paye: '',
  lot_gagne: '', statut: '', statut_heure: null, date_commande: ''
}

function calculerAge(dob) {
  if (!dob) return null
  const today = new Date()
  const birth = new Date(dob)
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
  return age
}

function formatDateTime(ts) {
  if (!ts) return '--'
  return new Date(ts).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}

export default function Inscrits({ isAdmin = false }) {
  const [coureurs, setCoureurs] = useState([])
  const [courses, setCourses] = useState([])
  const [retraits, setRetraits] = useState([])
  const [lots, setLots] = useState([])
  const [bracelets, setBracelets] = useState([])
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)
  const [loading, setLoading] = useState(true)
  const [mode, setMode] = useState('view')
  const [form, setForm] = useState(CHAMPS_VIDES)
  const [saving, setSaving] = useState(false)
  const [filterCourse, setFilterCourse] = useState('')
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [confirmSuppr, setConfirmSuppr] = useState(null)
  const [suppressing, setSuppressing] = useState(false)

  useEffect(() => {
    fetchAll()
    const sub = supabase
      .channel('inscrits-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'coureurs' }, fetchAll)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'dossards_retraits' }, fetchAll)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'lots' }, fetchAll)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bracelets' }, fetchAll)
      .subscribe()
    return () => supabase.removeChannel(sub)
  }, [])

  async function fetchAll() {
    const [c, co, r, l, b] = await Promise.all([
      supabase.from('coureurs').select('*, courses(nom, lot_coureur, lot_tombola)').order('nom'),
      supabase.from('courses').select('*').order('nom'),
      supabase.from('dossards_retraits').select('*').eq('invalide', false),
      supabase.from('lots').select('*').eq('invalide', false),
      supabase.from('bracelets').select('*').eq('invalide', false),
    ])
    setCoureurs(c.data || [])
    setCourses(co.data || [])
    setRetraits(r.data || [])
    setLots(l.data || [])
    setBracelets(b.data || [])
    setLoading(false)
  }

  function getDossardRetire(coureurId) { return retraits.find(r => r.coureur_id === coureurId) }
  function getLot(coureurId, type) { return lots.find(l => l.coureur_id === coureurId && l.type === type) }
  function getBracelet(coureurId) { return bracelets.find(b => b.coureur_id === coureurId) }

  async function toggleDossard(coureur) {
    const existing = getDossardRetire(coureur.id)
    if (existing) {
      await supabase.from('dossards_retraits').update({ invalide: true, invalide_le: new Date().toISOString() }).eq('id', existing.id)
    } else {
      await supabase.from('dossards_retraits').insert({ coureur_id: coureur.id, timestamp: new Date().toISOString() })
    }
    fetchAll()
  }

  async function toggleLot(coureur, type) {
    const existing = getLot(coureur.id, type)
    if (existing) {
      await supabase.from('lots').update({ invalide: true, invalide_le: new Date().toISOString() }).eq('id', existing.id)
    } else {
      await supabase.from('lots').insert({ coureur_id: coureur.id, type, timestamp: new Date().toISOString() })
    }
    fetchAll()
  }

  async function toggleBracelet(coureur) {
    const existing = getBracelet(coureur.id)
    if (existing) {
      await supabase.from('bracelets').update({ invalide: true, invalide_le: new Date().toISOString() }).eq('id', existing.id)
    } else {
      await supabase.from('bracelets').insert({ coureur_id: coureur.id, timestamp: new Date().toISOString() })
    }
    fetchAll()
  }

  async function changerStatut(coureur, nouveauStatut) {
    const ancienStatut = coureur.statut
    const now = new Date().toISOString()
    if (!nouveauStatut) {
      await supabase.from('coureurs').update({ statut: null, statut_heure: null }).eq('id', coureur.id)
      await supabase.from('detections')
        .update({ invalide: false, motif_invalidation: null, invalide_par: null, invalide_le: null })
        .eq('coureur_id', coureur.id)
        .eq('motif_invalidation', 'Statut ' + ancienStatut)
    } else {
      await supabase.from('coureurs').update({ statut: nouveauStatut, statut_heure: now }).eq('id', coureur.id)
      await supabase.from('detections')
        .update({ invalide: true, motif_invalidation: 'Statut ' + nouveauStatut, invalide_par: 'systeme', invalide_le: now })
        .eq('coureur_id', coureur.id)
        .eq('invalide', false)
    }
    fetchAll()
  }

  async function saveCoureur() {
    setSaving(true)
    const payload = {
      ...form,
      nb_repas: parseInt(form.nb_repas) || 0,
      prix_paye: parseFloat(form.prix_paye) || null,
      dob: form.dob || null,
      age: form.dob ? calculerAge(form.dob) : null,
      statut: form.statut || null,
    }
    if (mode === 'add') {
      const { error } = await supabase.from('coureurs').insert(payload)
      if (!error) { setMode('view'); setForm(CHAMPS_VIDES); setSelected(null) }
    } else if (mode === 'edit' && selected) {
      const ancienCoureur = coureurs.find(c => c.id === selected.id)
      const ancienDossard = ancienCoureur?.dossard || null
      const nouveauDossard = form.dossard?.trim() || null
      const courseChangee = ancienCoureur && form.course_id && ancienCoureur.course_id !== form.course_id

      // Dossard vidé → libérer dans dossards_disponibles
      if (ancienDossard && !nouveauDossard) {
        await supabase.from('dossards_disponibles')
          .update({ attribue: false, coureur_id: null })
          .eq('coureur_id', ancienCoureur.id)
        payload.dossard = null
      }
      // Course changée avec dossard conservé → basculer le dossard dans la nouvelle course
      if (courseChangee && nouveauDossard) {
        await supabase.from('dossards_disponibles')
          .update({ course_id: form.course_id })
          .eq('dossard', nouveauDossard)
      }

      const { error } = await supabase.from('coureurs').update(payload).eq('id', selected.id)
      if (!error) { setMode('view') }
    }
    setSaving(false)
    fetchAll()
  }

  async function prepareSupprimer(ids) {
    const idArr = [...ids]
    const { data: dets } = await supabase.from('detections')
      .select('id, coureur_id')
      .in('coureur_id', idArr)
      .eq('invalide', false)
    const coureursASuppr = coureurs.filter(c => ids.has(c.id))
    setConfirmSuppr({ ids: idArr, coureurs: coureursASuppr, detections: dets || [] })
  }

  async function executerSupprimer() {
    if (!confirmSuppr) return
    setSuppressing(true)
    const { ids } = confirmSuppr
    await supabase.from('dossards_disponibles').update({ attribue: false, coureur_id: null }).in('coureur_id', ids)
    await supabase.from('coureurs').update({ dossard: null }).in('id', ids)
    await supabase.from('detections').delete().in('coureur_id', ids)
    await supabase.from('dossards_retraits').delete().in('coureur_id', ids)
    await supabase.from('lots').delete().in('coureur_id', ids)
    await supabase.from('bracelets').delete().in('coureur_id', ids)
    await supabase.from('coureurs').delete().in('id', ids)
    setConfirmSuppr(null)
    setSelectedIds(new Set())
    setSelected(null)
    setSuppressing(false)
    fetchAll()
  }

  function toggleSelect(id, e) {
    e.stopPropagation()
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function startEdit(coureur) {
    setForm({
      dossard: coureur.dossard || '', nom: coureur.nom || '', prenom: coureur.prenom || '',
      course_id: coureur.course_id || '', genre: coureur.genre || '', type_vtt: coureur.type_vtt || '',
      nb_repas: coureur.nb_repas || 0, dob: coureur.dob || '', club: coureur.club || '',
      email: coureur.email || '', telephone: coureur.telephone || '', cp: coureur.cp || '',
      paye: coureur.paye || false, paye_par: coureur.paye_par || '', prix_paye: coureur.prix_paye || '',
      lot_gagne: coureur.lot_gagne || '', statut: coureur.statut || '', statut_heure: coureur.statut_heure || null, date_commande: coureur.date_commande || '',
    })
    setMode('edit')
  }

  function startAdd() {
    setForm(CHAMPS_VIDES)
    setSelected(null)
    setMode('add')
  }

  function getTodos(coureur) {
    const todos = []
    const course = coureur.courses
    if (!getDossardRetire(coureur.id)) todos.push('dossard')
    if (coureur.nb_repas > 0 && !getBracelet(coureur.id)) todos.push('repas')
    if (course?.lot_coureur && !getLot(coureur.id, 'coureur')) todos.push('lot_coureur')
    if (course?.lot_tombola && coureur.lot_gagne && !getLot(coureur.id, 'tombola')) todos.push('lot_tombola')
    return todos
  }

  const filtered = coureurs.filter(c => {
    const q = search.toLowerCase()
    const matchSearch = (
      c.dossard?.toLowerCase().includes(q) ||
      c.nom?.toLowerCase().includes(q) ||
      c.prenom?.toLowerCase().includes(q)
    )
    const matchCourse = !filterCourse || c.course_id === filterCourse
    return matchSearch && matchCourse
  })

  if (loading) return <div className="loading">Chargement...</div>

  const showForm = mode === 'add' || mode === 'edit'
  const coureurSelectionne = selected ? coureurs.find(c => c.id === selected.id) : null
  const hasDetail = showForm || !!coureurSelectionne

  return (
    <div className={'inscrits' + (hasDetail ? ' has-detail' : '')}>

      {confirmSuppr && (
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div style={{background:'#fff', borderRadius:12, padding:28, maxWidth:480, width:'90%', boxShadow:'0 8px 32px rgba(0,0,0,0.2)'}}>
            <h3 style={{marginBottom:12, color:'#c0392b', display:'flex', alignItems:'center', gap:8}}>
              <Trash2 size={20}/> Supprimer {confirmSuppr.coureurs.length} coureur{confirmSuppr.coureurs.length>1?'s':''}
            </h3>
            <div style={{marginBottom:12, maxHeight:150, overflowY:'auto'}}>
              {confirmSuppr.coureurs.map(c => (
                <div key={c.id} style={{fontSize:13, padding:'3px 8px', background:'#fdecea', borderRadius:4, marginBottom:2}}>
                  <strong>{c.nom} {c.prenom}</strong>
                  {c.dossard ? <span style={{color:'#888'}}> — dossard {c.dossard}</span> : ''}
                  {c.courses?.nom ? <span style={{color:'#888'}}> ({c.courses.nom})</span> : ''}
                </div>
              ))}
            </div>
            {confirmSuppr.detections.length > 0 ? (
              <div style={{background:'#fff3cd', border:'1px solid #ffc107', borderRadius:6, padding:'10px 14px', marginBottom:14, fontSize:13}}>
                ⚠️ <strong>{confirmSuppr.detections.length} détection{confirmSuppr.detections.length>1?'s':''} chronométrique{confirmSuppr.detections.length>1?'s':''} seront supprimées</strong> — cette action est irréversible.
              </div>
            ) : (
              <div style={{color:'#555', fontSize:13, marginBottom:14}}>
                Aucune détection chronométrique associée. Les dossards seront libérés.
              </div>
            )}
            <div style={{display:'flex', gap:10}}>
              <button className="btn" style={{background:'#c0392b', color:'#fff', flex:1}} onClick={executerSupprimer} disabled={suppressing}>
                {suppressing ? 'Suppression...' : 'Confirmer la suppression'}
              </button>
              <button className="btn" style={{background:'#eee', flex:1}} onClick={() => setConfirmSuppr(null)} disabled={suppressing}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="inscrits-layout">

        <div className="inscrits-list">
          <div className="list-header">
            <div className="search-box">
              <Search size={16} />
              <input
                placeholder="Nom, prenom, dossard..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <button onClick={() => setSearch('')}><X size={14}/></button>}
            </div>
            <button className="btn success" onClick={startAdd}>
              <Plus size={16}/>
            </button>
          </div>

          <div className="course-filter">
            <select value={filterCourse} onChange={e => setFilterCourse(e.target.value)}>
              <option value="">Toutes les courses</option>
              {courses.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>

          <div className="list-stats" style={{display:'flex', alignItems:'center', flexWrap:'wrap', gap:8}}>
            <span>{filtered.length} / {coureurs.length} coureurs</span>
            {isAdmin && selectedIds.size > 0 && (<>
              <span style={{color:'#1a7a5e', fontWeight:600}}>{selectedIds.size} sélectionné{selectedIds.size>1?'s':''}</span>
              <button className="btn" style={{fontSize:11, padding:'2px 8px', background:'#fdecea', color:'#c0392b', border:'1px solid #e74c3c', display:'flex', alignItems:'center', gap:4}}
                onClick={() => prepareSupprimer(selectedIds)}><Trash2 size={12}/> Supprimer</button>
              <button className="btn" style={{fontSize:11, padding:'2px 8px', background:'#eee'}}
                onClick={() => setSelectedIds(new Set())}>Désélectionner</button>
            </>)}
            {isAdmin && filtered.length > 0 && selectedIds.size === 0 && (
              <button className="btn" style={{fontSize:11, padding:'2px 8px', background:'#fdecea', color:'#c0392b', border:'1px solid #e74c3c', display:'flex', alignItems:'center', gap:4}}
                onClick={() => prepareSupprimer(new Set(filtered.map(c => c.id)))}><Trash2 size={12}/> Supprimer tous ({filtered.length})</button>
            )}
          </div>

          <div className="list-items">
            {filtered.map(coureur => {
              const todos = getTodos(coureur)
              const keys = ['dossard', 'repas', 'lot_coureur', 'lot_tombola']
              return (
                <div
                  key={coureur.id}
                  className={'coureur-item ' + (selected?.id === coureur.id ? 'active' : '') + (todos.length === 0 ? ' done' : '')}
                  onClick={() => { setSelected(coureur); setMode('view') }}
                >
                  <div className="coureur-item-main">
                    <div style={{display:'flex', alignItems:'center', gap:6}}>
                      <input type="checkbox" checked={selectedIds.has(coureur.id)}
                        onChange={e => toggleSelect(coureur.id, e)}
                        onClick={e => e.stopPropagation()}
                        style={{flexShrink:0, cursor:'pointer'}} />
                      <span className="coureur-name">{coureur.nom} {coureur.prenom}</span>
                      {coureur.statut && (
                        <span className={'statut-badge ' + (coureur.statut === 'DNS' ? 'dns' : 'dnf')}>
                          {coureur.statut}
                        </span>
                      )}
                    </div>
                    <span className="coureur-meta">#{coureur.dossard} | {coureur.courses?.nom}</span>
                    {!coureur.paye && <span className="badge-unpaid">Non paye</span>}
                  </div>
                  <div className="coureur-icons">
                    {keys.map(key => {
                      const shouldShow = key === 'dossard' ? true
                        : key === 'repas' ? coureur.nb_repas > 0
                        : key === 'lot_coureur' ? coureur.courses?.lot_coureur
                        : (coureur.courses?.lot_tombola && coureur.lot_gagne) ? true : false
                      if (!shouldShow) return null
                      const isDone = !todos.includes(key)
                      return (
                        <span key={key} className={'icon-chip ' + (isDone ? 'icon-chip-done' : 'icon-chip-todo')}>
                          {ICONS[key]}
                        </span>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="inscrits-detail">

          {showForm && (
            <div className="detail-card">
              <div className="detail-header">
                <button className="mobile-back-btn" style={{display:'none'}} onClick={() => { setMode('view'); setForm(CHAMPS_VIDES) }}>
                  <ArrowLeft size={20}/>
                </button>
                <h2>{mode === 'add' ? 'Nouveau coureur' : 'Modifier ' + form.nom + ' ' + form.prenom}</h2>
                <button className="btn-icon" onClick={() => { setMode('view'); setForm(CHAMPS_VIDES) }}><X size={18}/></button>
              </div>
              <div className="form-grid">
                <Field label="Dossard *" value={form.dossard} onChange={v => setForm({...form, dossard: v})} />
                <Field label="Nom *" value={form.nom} onChange={v => setForm({...form, nom: v})} />
                <Field label="Prenom" value={form.prenom} onChange={v => setForm({...form, prenom: v})} />
                <div className="field-row">
                  <label>Course *</label>
                  <select value={form.course_id} onChange={e => setForm({...form, course_id: e.target.value})}>
                    <option value="">Choisir...</option>
                    {courses.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                </div>
                <div className="field-row">
                  <label>Genre</label>
                  <select value={form.genre} onChange={e => setForm({...form, genre: e.target.value})}>
                    <option value="">--</option>
                    <option>Masculin</option>
                    <option>Feminin</option>
                  </select>
                </div>
                <div className="field-row">
                  <label>Type VTT</label>
                  <select value={form.type_vtt} onChange={e => setForm({...form, type_vtt: e.target.value})}>
                    <option value="">--</option>
                    <option>VTT</option>
                    <option>VTTAE</option>
                  </select>
                </div>
                <div className="field-row">
                  <label>Naissance</label>
                  <div style={{display:'flex', gap:8, alignItems:'center', flex:1}}>
                    <input type="date" value={form.dob} onChange={e => setForm({...form, dob: e.target.value})} style={{flex:1}} />
                    {form.dob && <span className="age-badge">{calculerAge(form.dob)} ans</span>}
                  </div>
                </div>
                <Field label="Club" value={form.club} onChange={v => setForm({...form, club: v})} />
                <Field label="Email" value={form.email} onChange={v => setForm({...form, email: v})} type="email" />
                <Field label="Tel" value={form.telephone} onChange={v => setForm({...form, telephone: v})} />
                <Field label="CP" value={form.cp} onChange={v => setForm({...form, cp: v})} />
                <Field label="Nb repas" value={form.nb_repas} onChange={v => setForm({...form, nb_repas: v})} type="number" />
                <Field label="Prix paye" value={form.prix_paye} onChange={v => setForm({...form, prix_paye: v})} type="number" />
                <Field label="Paye par" value={form.paye_par} onChange={v => setForm({...form, paye_par: v})} />
                <div className="field-row">
                  <label>Paye</label>
                  <input type="checkbox" checked={form.paye} onChange={e => setForm({...form, paye: e.target.checked})} />
                </div>
                <Field label="Lot gagne" value={form.lot_gagne} onChange={v => setForm({...form, lot_gagne: v})} />
                <div className="field-row">
                  <label>Date commande</label>
                  <input type="date" value={form.date_commande ? form.date_commande.substring(0,10) : ''} onChange={e => setForm({...form, date_commande: e.target.value})} />
                </div>
              </div>
              <div style={{marginTop: 16, display:'flex', gap:8}}>
                <button className="btn success full" onClick={saveCoureur} disabled={saving}>
                  <Save size={16}/> {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
                <button className="btn" style={{background:'#eee'}} onClick={() => { setMode('view'); setForm(CHAMPS_VIDES) }}>
                  Annuler
                </button>
              </div>
            </div>
          )}

          {!showForm && !coureurSelectionne && (
            <div className="detail-empty">
              <Search size={40} opacity={0.2}/>
              <p>Selectionnez un coureur</p>
            </div>
          )}

          {!showForm && coureurSelectionne && (() => {
            const coureur = coureurSelectionne
            const course = coureur.courses
            const dossardRetire = getDossardRetire(coureur.id)
            const bracelet = getBracelet(coureur.id)
            const lotCoureur = getLot(coureur.id, 'coureur')
            const lotTombola = getLot(coureur.id, 'tombola')
            const age = coureur.dob ? calculerAge(coureur.dob) : coureur.age

            return (
              <div className="detail-card">
                <div className="detail-header">
                  <button className="mobile-back-btn" style={{display:'none'}} onClick={() => setSelected(null)}>
                    <ArrowLeft size={20}/>
                  </button>
                  <h2>{coureur.nom} {coureur.prenom}</h2>
                  <div style={{display:'flex', gap:8}}>
                    <button
                      className="btn"
                      style={{background:'#f0f0f0', fontSize:13, display:'flex', alignItems:'center', gap:6}}
                      onClick={() => startEdit(coureur)}
                    >
                      <Edit2 size={14}/> Modifier
                    </button>
                    {isAdmin && (
                      <button
                        className="btn"
                        style={{background:'#fdecea', color:'#c0392b', border:'1px solid #e74c3c', fontSize:13, display:'flex', alignItems:'center', gap:6}}
                        onClick={() => prepareSupprimer(new Set([coureur.id]))}
                      >
                        <Trash2 size={14}/> Supprimer
                      </button>
                    )}
                    <button className="btn-icon desktop-only-close" onClick={() => setSelected(null)}><X size={18}/></button>
                  </div>
                </div>

                <div className="detail-meta">
                  <span className="badge">#{coureur.dossard}</span>
                  <span className="badge">{course?.nom}</span>
                  {coureur.genre && <span className="badge">{coureur.genre}</span>}
                  {coureur.type_vtt && <span className="badge">{coureur.type_vtt}</span>}
                  {age && <span className="badge">{age} ans</span>}
                  {coureur.club && <span className="badge">{coureur.club}</span>}
                  {coureur.paye
                    ? <span className="badge" style={{background:'#d4edda', color:'#155724'}}>Paye</span>
                    : <span className="badge" style={{background:'#f8d7da', color:'#842029'}}>Non paye</span>
                  }
                </div>

                <div className="info-grid">
                  {coureur.email && <div className="info-item"><span className="info-label">Email</span><span>{coureur.email}</span></div>}
                  {coureur.telephone && <div className="info-item"><span className="info-label">Tel</span><span>{coureur.telephone}</span></div>}
                  {coureur.cp && <div className="info-item"><span className="info-label">CP</span><span>{coureur.cp}</span></div>}
                  {coureur.prix_paye && <div className="info-item"><span className="info-label">Prix</span><span>{coureur.prix_paye}EUR {coureur.paye_par ? '(' + coureur.paye_par + ')' : ''}</span></div>}
                  {coureur.date_commande && <div className="info-item"><span className="info-label">Inscrit le</span><span>{new Date(coureur.date_commande).toLocaleDateString('fr-FR')}</span></div>}
                  {coureur.lot_gagne && <div className="info-item"><span className="info-label">Lot gagne</span><span>{coureur.lot_gagne}</span></div>}
                </div>

                <div className="statut-section">
                  <div className="statut-header">
                    <strong>Statut</strong>
                    {coureur.statut && coureur.statut_heure && (
                      <span className="statut-time">depuis {formatDateTime(coureur.statut_heure)}</span>
                    )}
                  </div>
                  <div className="statut-buttons">
                    <button className={'statut-btn ' + (!coureur.statut ? 'active-normal' : '')} onClick={() => changerStatut(coureur, null)}>Normal</button>
                    <button className={'statut-btn ' + (coureur.statut === 'DNS' ? 'active-dns' : '')} onClick={() => changerStatut(coureur, coureur.statut === 'DNS' ? null : 'DNS')}>DNS</button>
                    <button className={'statut-btn ' + (coureur.statut === 'DNF' ? 'active-dnf' : '')} onClick={() => changerStatut(coureur, coureur.statut === 'DNF' ? null : 'DNF')}>DNF</button>
                  </div>
                </div>

                <div className="detail-actions">
                  <ActionRow icon={ICONS.dossard} label="Dossard" done={!!dossardRetire} time={dossardRetire?.timestamp} onToggle={() => toggleDossard(coureur)} />
                  {coureur.nb_repas > 0 && (
                    <ActionRow icon={ICONS.repas} label={'Repas (' + coureur.nb_repas + ')'} done={!!bracelet} time={bracelet?.timestamp} onToggle={() => toggleBracelet(coureur)} />
                  )}
                  {course?.lot_coureur && (
                    <ActionRow icon={ICONS.lot_coureur} label="Lot coureur" done={!!lotCoureur} time={lotCoureur?.timestamp} onToggle={() => toggleLot(coureur, 'coureur')} />
                  )}
                  {course?.lot_tombola && coureur.lot_gagne && (
                    <ActionRow icon={ICONS.lot_tombola} label="Lot tombola" done={!!lotTombola} time={lotTombola?.timestamp} onToggle={() => toggleLot(coureur, 'tombola')} />
                  )}
                </div>
              </div>
            )
          })()}
        </div>
      </div>
    </div>
  )
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div className="field-row">
      <label>{label}</label>
      <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} />
    </div>
  )
}

function ActionRow({ icon, label, done, time, onToggle }) {
  return (
    <div className={'action-row ' + (done ? 'done' : 'todo')}>
      <div className="action-label">
        <span className="action-icon">{icon}</span>
        <div>
          <strong>{label}</strong>
          {done && time && (
            <div className="action-time">
              {new Date(time).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>
      </div>
      <button className={'toggle-btn ' + (done ? 'active' : '')} onClick={onToggle}>
        {done ? <Check size={18}/> : <AlertCircle size={18}/>}
      </button>
    </div>
  )
}
